import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client.js';
import { LANGUAGES, setLanguage, toLanguage, type Language } from '../i18n.js';
import { ME_KEY } from '../session.js';

/** Short labels as on the landing page (DE · EN · UA). */
const SHORT: Record<Language, string> = { de: 'DE', en: 'EN', uk: 'UA' };

/**
 * Changes the UI language (T-41: three buttons instead of a drop-down, as in the mockups); signed-in
 * users keep it for the app and – until chosen separately – for their e-mails.
 */
export function LanguageSwitch({
  signedIn,
  className = '',
}: {
  signedIn: boolean;
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const client = useQueryClient();
  const current = toLanguage(i18n.language);
  const change = async (language: Language) => {
    if (language === current) return;
    setLanguage(language);
    if (signedIn) {
      await api.PATCH('/me', { body: { locale: language } });
      await client.invalidateQueries({ queryKey: ME_KEY });
    }
  };
  return (
    <div
      role="group"
      aria-label={t('nav.language')}
      className={`inline-flex overflow-hidden rounded-lg border border-slate-300 bg-white ${className}`}
    >
      {LANGUAGES.map((l, i) => (
        <button
          key={l}
          type="button"
          lang={l}
          aria-pressed={l === current}
          aria-label={t(`lang.${l}`)}
          title={t(`lang.${l}`)}
          onClick={() => void change(l)}
          className={`min-h-11 min-w-11 flex-1 px-3 text-sm font-bold tracking-wide ${i > 0 ? 'border-l border-slate-300' : ''} ${l === current ? 'bg-slate-900 text-white' : 'text-slate-800 hover:bg-slate-100'} focus-visible:relative focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700`}
        >
          {SHORT[l]}
        </button>
      ))}
    </div>
  );
}
